// TODO: Volver aquí para arreglar el botón de pánico

// src/exercises/SpacyPanicButtons.jsx
// ══════════════════════════════════════════════════════════════════════════════
// 🆘 SISTEMA DE PÁNICO NEURONAL v2.2
//    — Acordeón: expande HACIA ABAJO (no comprime el chat)
//    — onToggle callback para que el padre haga scrollToBottom
//    — Estado pausado conserva toda la lógica anterior
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import './spacy-panic.css';

// ─── Definición de botones ────────────────────────────────────────────────────

const FREE_BUTTONS = [
    {
        id: 'rescue',
        emoji: '⏸️',
        label: 'Pausa',
        sublabel: 'Salir del personaje',
        pausedEmoji: '▶️',
        pausedLabel: 'Pausado',
        pausedSublabel: "Escribe 'continuar'",
        color: '#ef4444',
        pulse: true,
        tooltip: 'Spacy rompe el personaje y te ayuda',
        pausedTooltip: "Escribe 'continuar' para reanudar la escena",
    },
    {
        id: 'phrase',
        emoji: '💬',
        label: 'Frase',
        sublabel: 'La frase perfecta',
        color: '#f59e0b',
        pulse: false,
        tooltip: '3 frases listas para usar ahora mismo',
    },
    {
        id: 'script',
        emoji: '🎭',
        label: 'Guión',
        sublabel: 'Mini diálogo',
        color: '#a78bfa',
        pulse: false,
        tooltip: 'Un guión de cómo puede seguir la escena',
    },
    {
        id: 'vocab',
        emoji: '🔤',
        label: 'Palabras',
        sublabel: 'Del escenario',
        color: '#6366f1',
        pulse: false,
        tooltip: 'Vocabulario específico para este rol',
    },
    {
        id: 'hint',
        emoji: '💡',
        label: 'Pista',
        sublabel: 'Solo un empujón',
        color: '#22c55e',
        pulse: false,
        tooltip: 'Una pista sutil sin romper la inmersión',
    },
];

const TUTOR_BUTTONS = [
    {
        id: 'rescue',
        emoji: '🆘',
        label: 'Rescate',
        sublabel: 'Estoy perdido',
        color: '#ef4444',
        pulse: true,
        tooltip: 'Spacy te rescata desde cero',
    },
    {
        id: 'hint',
        emoji: '💡',
        label: 'Pista',
        sublabel: 'Dame un clue',
        color: '#f59e0b',
        pulse: false,
        tooltip: 'Un susurro, no la respuesta',
    },
    {
        id: 'vocab',
        emoji: '🔤',
        label: 'Palabras',
        sublabel: 'Vocabulario ahora',
        color: '#6366f1',
        pulse: false,
        tooltip: 'Las palabras exactas que necesitas',
    },
    {
        id: 'model',
        emoji: '📝',
        label: 'Modelo',
        sublabel: 'Muéstrame un ejemplo',
        color: '#22c55e',
        pulse: false,
        tooltip: 'Oración completa para copiar y adaptar',
    },
    {
        id: 'simplify',
        emoji: '🔄',
        label: 'Más fácil',
        sublabel: 'No entendí nada',
        color: '#60a5fa',
        pulse: false,
        tooltip: 'Re-explicar más simple y despacio',
    },
];

// ─── Tooltip ─────────────────────────────────────────────────────────────────

function Tooltip({ text, visible }) {
    if (!visible) return null;
    return <div className="spb-tooltip">{text}</div>;
}

// ─── Botón individual ─────────────────────────────────────────────────────────

function PanicButton({ btn, isLoading, isActive, disabled, isPaused, onClick }) {
    const [showTooltip, setShowTooltip] = useState(false);

    const isRescuePaused = btn.id === 'rescue' && isPaused;

    const displayEmoji = isRescuePaused ? (btn.pausedEmoji || btn.emoji) : btn.emoji;
    const displayLabel = isRescuePaused ? (btn.pausedLabel || btn.label) : btn.label;
    const displaySublabel = isRescuePaused ? (btn.pausedSublabel || btn.sublabel) : btn.sublabel;
    const displayTooltip = isRescuePaused ? (btn.pausedTooltip || btn.tooltip) : btn.tooltip;

    const isEffectivelyDisabled = disabled || isRescuePaused;

    return (
        <div
            className="spb-btn-wrapper"
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
        >
            <button
                className={[
                    'spb-btn',
                    btn.pulse && !disabled && !isRescuePaused ? 'spb-pulse' : '',
                    isLoading ? 'spb-loading' : '',
                    isActive ? 'spb-active' : '',
                    isEffectivelyDisabled ? 'spb-disabled' : '',
                    isRescuePaused ? 'spb-paused' : '',
                ].filter(Boolean).join(' ')}
                style={{ '--btn-color': btn.color }}
                onClick={() => !isEffectivelyDisabled && onClick(btn.id)}
                disabled={isEffectivelyDisabled}
                aria-label={displayTooltip}
            >
                {btn.pulse && !isRescuePaused && <span className="spb-ring" />}

                <span className="spb-emoji">
                    {isLoading ? <span className="spb-spinner" /> : displayEmoji}
                </span>

                <span className="spb-labels">
                    <span className="spb-label">{displayLabel}</span>
                    <span className="spb-sublabel">{displaySublabel}</span>
                </span>
            </button>

            <Tooltip text={displayTooltip} visible={showTooltip && !disabled} />
        </div>
    );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SpacyPanicButtons({
    mode = 'tutor',
    disabled = false,
    isPaused = false,
    onHelp,
    onToggle, // 🆕 v2.2 — callback(isOpen: boolean) para que el padre haga scroll
}) {
    const [loadingType, setLoadingType] = useState(null);
    const [lastActive, setLastActive] = useState(null);
    const [isOpen, setIsOpen] = useState(false);

    const buttons = mode === 'free' ? FREE_BUTTONS : TUTOR_BUTTONS;

    // Cuando el estado es pausado, forzar apertura para que el alumno vea el mensaje
    const effectivelyOpen = isOpen || isPaused;

    const handleClick = async (type) => {
        if (loadingType || disabled) return;
        setLoadingType(type);
        setLastActive(type);

        // ── PULSAR CIERRA LA BARRA ────────────────────────────────────────
        //  Rob, 29-09-2026: «quiero que cuando le den clic a un botón la barra
        //  se esconda, para que cuando tengan la respuesta no moleste; y si
        //  quieren apretar otro, que abran las opciones, den clic y se cierre
        //  para ver la respuesta».
        //
        //  Se cierra AHORA y no al terminar: la respuesta tarda unos segundos
        //  en llegar y es justo entonces cuando la barra tapa lo que el alumno
        //  espera. Cerrarla después la dejaría estorbando todo ese rato.
        //
        //  Cuando está en pausa no se cierra, y no hace falta comprobarlo aquí:
        //  `effectivelyOpen` la mantiene abierta mientras `isPaused`, porque
        //  ese mensaje hay que poder leerlo.
        setIsOpen(false);
        if (typeof onToggle === 'function') setTimeout(() => onToggle(false), 50);

        try {
            await onHelp(type);
        } finally {
            setLoadingType(null);
        }
    };

    const toggleAccordion = () => {
        // No permitir colapsar si está en estado pausado
        if (isPaused) return;

        const nextOpen = !isOpen;
        setIsOpen(nextOpen);

        // 🆕 Notificar al padre para que haga scroll al bottom cuando abre
        if (typeof onToggle === 'function') {
            // Pequeño delay para que la animación CSS empiece antes del scroll
            setTimeout(() => onToggle(nextOpen), 50);
        }
    };

    return (
        <div
            className={[
                'spb-container',
                effectivelyOpen ? 'spb-accordion-open' : 'spb-accordion-closed',
                isPaused ? 'spb-paused-mode' : '',
            ].filter(Boolean).join(' ')}
        >
            {/* ── Header — trigger del acordeón ── */}
            <button
                className="spb-header spb-header-toggle"
                onClick={toggleAccordion}
                aria-expanded={effectivelyOpen}
                aria-label={effectivelyOpen ? 'Ocultar ayuda' : 'Mostrar ayuda'}
                disabled={isPaused}
            >
                <span className="spb-header-icon">🛟</span>
                <span className="spb-header-text">Ayuda</span>

                <span className="spb-header-hint">
                    {isPaused
                        ? "⏸ Escena pausada — escribe 'continuar'"
                        : disabled
                            ? 'Esperando a Spacy...'
                            : effectivelyOpen
                                ? 'Toca para ocultar'
                                : '¿Necesitas ayuda?'
                    }
                </span>

                {/* Indicador de botones disponibles cuando está cerrado */}
                {!effectivelyOpen && !isPaused && (
                    <span className="spb-pill">{buttons.length}</span>
                )}

                {/* Chevron animado */}
                {!isPaused && (
                    <span className={`spb-chevron ${effectivelyOpen ? 'spb-chevron-open' : ''}`}>
                        ›
                    </span>
                )}
            </button>

            {/* ── Grid con transición de altura — expande hacia ABAJO ── */}
            <div
                className="spb-grid-wrapper"
                aria-hidden={!effectivelyOpen}
            >
                <div className="spb-grid">
                    {buttons.map(btn => (
                        <PanicButton
                            key={btn.id}
                            btn={btn}
                            isLoading={loadingType === btn.id}
                            isActive={lastActive === btn.id && !loadingType && !isPaused}
                            disabled={disabled || (loadingType !== null && loadingType !== btn.id)}
                            isPaused={isPaused}
                            onClick={handleClick}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}